import { API_CONFIG, API_ENDPOINTS } from '@/constants/config';

export interface Divider {
  id?: number;
  x1position: number;
  y1position: number;
  x2position: number;
  y2position: number;
  thickness: number;
  type: string;
  rotation: number;
  color: string;
  floorId: number;
}

export interface DividerRequest {
  id?: number;
  x1position: number;
  y1position: number;
  x2position: number;
  y2position: number;
  thickness: number;
  type: string;
  rotation: number;
  color: string;
  floorId: number;
}

class DividerApiService {
  private baseUrl: string;

  constructor() {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://admin.grandpittu.lk');
    this.baseUrl = `${baseUrl}${API_CONFIG.SLUG}`;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      });

      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        const text = await response.text();
        if (response.ok && (text.includes('successfully') || text.includes('saved'))) {
          return { success: true, message: text } as unknown as T;
        }
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} - ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch {
            // Keep original message
          }
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Save a new divider
   */
  async saveDivider(divider: DividerRequest): Promise<string> {
    return this.makeRequest<string>(API_ENDPOINTS.DIVIDER.BASE, {
      method: 'POST',
      body: JSON.stringify(divider),
    });
  }

  /**
   * Get all dividers
   */
  async getAllDividers(): Promise<Divider[]> {
    return this.makeRequest<Divider[]>(API_ENDPOINTS.DIVIDER.GET_ALL);
  }

  /**
   * Get divider by ID
   */
  async getDividerById(id: number): Promise<Divider> {
    return this.makeRequest<Divider>(API_ENDPOINTS.DIVIDER.BY_ID(id));
  }

  /**
   * Update divider
   */
  async updateDivider(id: number, divider: DividerRequest): Promise<Divider> {
    return this.makeRequest<Divider>(API_ENDPOINTS.DIVIDER.BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(divider),
    });
  }

  /**
   * Delete divider
   */
  async deleteDivider(id: number): Promise<string> {
    return this.makeRequest<string>(API_ENDPOINTS.DIVIDER.BY_ID(id), {
      method: 'DELETE',
    });
  }
}

export const dividerApi = new DividerApiService();
